import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import BeatLoader from "react-spinners/BeatLoader";
import Tippy from "@tippyjs/react";
import { MdAdd, MdDelete, MdModeEditOutline } from "react-icons/md";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Modal from "../../components/Modal";
import Constant from "../../Constant";
import TX_TYPES from "../../constants/transaction-types";
import { fetchCountries } from "../../services/countries";
import {
  deleteTransactionConfig,
  fetchMerchantTransactionConfigs,
  updateTransactionConfig,
  upsertMerchantTransactionConfig,
} from "../../services/transaction-config";
import "../../pages/setting/index.scss";

const tableHead = [
  { id: "1", label: "Country", minWidth: 120 },
  { id: "2", label: "Transaction type", minWidth: 140 },
  { id: "3", label: "Rate (%)", minWidth: 80 },
  { id: "4", label: "Action", minWidth: 100 },
];

const EMPTY_CREATE = { country: "", type: "", config_rate: "" };
const EMPTY_FILTERS = { type: "", country: "" };

const MerchantFeeSection = ({ merchantUserId, authToken, onChanged }) => {
  const [configs, setConfigs] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [editRate, setEditRate] = useState("");
  const [currentConfig, setCurrentConfig] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const createRef = useRef(null);
  const editRef = useRef(null);
  const deleteRef = useRef(null);

  const loadConfigs = (token = authToken) => {
    if (!merchantUserId || !token) return;
    setLoading(true);
    fetchMerchantTransactionConfigs(merchantUserId, token)
      .then((res) => {
        if (res.status_code === 200) {
          setConfigs(res.data?.configs || []);
        } else {
          setConfigs([]);
          if (res.status_code !== 404) {
            toast.error(`${res.message}`);
          }
        }
      })
      .catch((err) => {
        console.log("ERROR: ", err);
        setConfigs([]);
        toast.error("Failed to load merchant fee rates");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const loadCountries = (token = authToken) => {
    if (!token) return;
    fetchCountries(token, { limit: 100 })
      .then((res) => {
        if (res.status_code === 200) {
          setCountries(res.data?.countries || []);
        }
      })
      .catch((err) => {
        console.log("ERROR: ", err);
      });
  };

  useEffect(() => {
    if (!merchantUserId || !authToken) return;
    loadConfigs(authToken);
    loadCountries(authToken);
  }, [merchantUserId, authToken]);

  const countryOptions = countries.filter(
    (item) => item.status === "ACTIVE" || item.name === "Nigeria"
  );

  const filterCountryOptions = Array.from(
    new Map(
      [
        ...countries,
        ...configs.map((row) => row.country).filter(Boolean),
      ]
        .filter((country) => country?.id)
        .map((country) => [country.id, country])
    ).values()
  ).sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const filteredConfigs = configs.filter((row) => {
    if (filters.type && row.type?.name !== filters.type) return false;
    if (filters.country && row.country?.id !== filters.country) return false;
    return true;
  });

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
  };

  const openCreateModal = () => {
    setCreateForm(EMPTY_CREATE);
    createRef.current.open();
  };

  const handleCreate = () => {
    const rate = Number(createForm.config_rate);
    if (!createForm.country) {
      toast.error("Select a country");
      return;
    }
    if (!createForm.type) {
      toast.error("Select a transaction type");
      return;
    }
    if (!Number.isFinite(rate) || rate < 0) {
      toast.error("Rate must be a number greater than or equal to 0");
      return;
    }

    setLoading(true);
    upsertMerchantTransactionConfig(
      merchantUserId,
      {
        country: createForm.country,
        type: createForm.type,
        config_rate: rate,
      },
      authToken
    )
      .then((result) => {
        if (result.status_code === 200) {
          toast.success(`${result.message}`);
          loadConfigs(authToken);
          onChanged?.();
          createRef.current.close();
        } else {
          toast.error(`${result.message}`);
        }
      })
      .catch((err) => {
        console.log("ERROR: ", err);
        toast.error("Failed to save merchant fee rate");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleUpdate = () => {
    const rate = Number(editRate);
    if (!currentConfig?.id) return;
    if (!Number.isFinite(rate) || rate < 0) {
      toast.error("Rate must be a number greater than or equal to 0");
      return;
    }

    setLoading(true);
    updateTransactionConfig(currentConfig.id, { config_rate: rate }, authToken)
      .then((result) => {
        if (result.status_code === 200) {
          toast.success(`${result.message}`);
          loadConfigs(authToken);
          onChanged?.();
          editRef.current.close();
        } else {
          toast.error(`${result.message}`);
        }
      })
      .catch((err) => {
        console.log("ERROR: ", err);
        toast.error("Failed to update merchant fee rate");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDelete = () => {
    if (!toDelete?.id) return;
    setLoading(true);
    deleteTransactionConfig(toDelete.id, authToken)
      .then((result) => {
        if (result.status_code === 200) {
          toast.success(
            `${result.message}. This merchant will use the global Settings rate for this country and type.`
          );
          loadConfigs(authToken);
          onChanged?.();
        } else {
          toast.error(`${result.message}`);
        }
      })
      .catch((err) => {
        console.log("ERROR: ", err);
        toast.error("Failed to delete merchant fee rate");
      })
      .finally(() => {
        setLoading(false);
        deleteRef.current.close();
      });
  };

  return (
    <div className="py-2 bg-[#fff] rounded-[8px] p-3">
      <p className="font-semibold">Fee rates</p>
      <p className="text-sm text-[#767676] mt-1 mb-4">
        Merchant-specific rates override global transaction rates from Settings
        for this merchant only. Removing an override restores the default rate.
      </p>

      <div className="creation_sec">
        <button className="creartbtn" type="button" onClick={openCreateModal}>
          Add rate
          <MdAdd className="text-2xl" />
        </button>
      </div>

      <div className="txn-filters">
        <div>
          <label htmlFor="merchant-fee-filter-country">Country</label>
          <select
            id="merchant-fee-filter-country"
            value={filters.country}
            onChange={(e) => updateFilter("country", e.target.value)}
          >
            <option value="">All</option>
            {filterCountryOptions.map((country) => (
              <option key={country.id} value={country.id}>
                {country.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="merchant-fee-filter-type">Transaction type</label>
          <select
            id="merchant-fee-filter-type"
            value={filters.type}
            onChange={(e) => updateFilter("type", e.target.value)}
          >
            <option value="">All</option>
            {TX_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
        <div className="txn-filters-actions">
          <button
            type="button"
            className="txn-filter-clear"
            onClick={clearFilters}
          >
            Clear
          </button>
        </div>
      </div>

      <Paper className="main" sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: 420 }} style={{ boxShadow: "none" }}>
          <Table stickyHeader aria-label="merchant fee rates">
            <TableHead>
              <TableRow>
                {tableHead.map((column) => (
                  <TableCell
                    key={column.id}
                    align="center"
                    style={{ minWidth: column.minWidth }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && filteredConfigs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No merchant-specific fee overrides yet.
                  </TableCell>
                </TableRow>
              )}
              {filteredConfigs.map((row) => (
                <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                  <TableCell align="center">
                    {row.country?.name || "—"}
                  </TableCell>
                  <TableCell align="center">{row.type?.name || "—"}</TableCell>
                  <TableCell align="center">{row.config_rate}%</TableCell>
                  <TableCell align="center">
                    <Tippy content="Edit" animation="fade">
                      <button
                        type="button"
                        onClick={() => {
                          setCurrentConfig(row);
                          setEditRate(
                            row.config_rate === 0 || row.config_rate
                              ? String(row.config_rate)
                              : ""
                          );
                          editRef.current.open();
                        }}
                        className="bg-[#00A5EE] mx-1.5 rounded-full text-[#000] p-2"
                      >
                        <MdModeEditOutline size={16} color="#fff" />
                      </button>
                    </Tippy>
                    <Tippy content="Delete" animation="fade">
                      <button
                        type="button"
                        onClick={() => {
                          setToDelete(row);
                          deleteRef.current.open();
                        }}
                        className="bg-[#E65B65] mx-1.5 rounded-full text-[#000] p-2"
                      >
                        <MdDelete size={16} color="#fff" />
                      </button>
                    </Tippy>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <BeatLoader
          color="#36d7b7"
          loading={loading}
          cssOverride={{ textAlign: "center", marginTop: "16px" }}
        />
      </Paper>

      <Modal
        title="Add merchant fee rate"
        onSubmit={handleCreate}
        onClose={() => setCreateForm(EMPTY_CREATE)}
        ref={createRef}
      >
        <div className="edit_form">
          <div className="field">
            <label htmlFor="merchant-fee-country">Country</label>
            <select
              id="merchant-fee-country"
              className="__form-input"
              value={createForm.country}
              onChange={(e) =>
                setCreateForm((prev) => ({ ...prev, country: e.target.value }))
              }
            >
              <option value="">Select a country</option>
              {countryOptions.map((country) => (
                <option key={country.id} value={country.id}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="merchant-fee-type">Transaction type</label>
            <select
              id="merchant-fee-type"
              className="__form-input"
              value={createForm.type}
              onChange={(e) =>
                setCreateForm((prev) => ({ ...prev, type: e.target.value }))
              }
            >
              <option value="">Select a type</option>
              {TX_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="merchant-fee-rate">Rate (%)</label>
            <input
              id="merchant-fee-rate"
              className="__form-input"
              type="number"
              min="0"
              step="0.01"
              value={createForm.config_rate}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  config_rate: e.target.value,
                }))
              }
            />
          </div>
        </div>
      </Modal>

      <Modal
        title="Update merchant fee rate"
        onSubmit={handleUpdate}
        onClose={() => setCurrentConfig(null)}
        ref={editRef}
      >
        <div className="edit_form">
          <div className="field">
            <label>Country</label>
            <p className="readonly-value">{currentConfig?.country?.name || "—"}</p>
          </div>
          <div className="field">
            <label>Transaction type</label>
            <p className="readonly-value">{currentConfig?.type?.name || "—"}</p>
          </div>
          <div className="field">
            <label htmlFor="merchant-fee-edit-rate">Rate (%)</label>
            <input
              id="merchant-fee-edit-rate"
              className="__form-input"
              type="number"
              min="0"
              step="0.01"
              value={editRate}
              onChange={(e) => setEditRate(e.target.value)}
            />
          </div>
          <BeatLoader
            color={Constant.spinner_color}
            loading={loading}
            className="__center_spinner"
            cssOverride={{
              textAlign: "center",
              marginTop: "20px",
            }}
          />
        </div>
      </Modal>

      <Modal
        title={`Delete ${toDelete?.type?.name || "rate"}`}
        onSubmit={handleDelete}
        onClose={() => setToDelete(null)}
        ref={deleteRef}
      >
        <h2 className="text-center text-red-700 text-xl tracking-wide">
          Warning!!!
        </h2>
        <h2>
          Are you sure you want to delete the {toDelete?.type?.name} override for{" "}
          {toDelete?.country?.name}? This merchant will use the global Settings
          rate for that country and type.
        </h2>
      </Modal>
    </div>
  );
};

export default MerchantFeeSection;
